// Refonte du suivi — date de début d'une phase activée par le nouveau suivi.
//
// Le nouveau suivi active la phase cible à la publication (ou à la première
// remise du document) SANS écrire de date dans les phases : la date ferait
// changer l'empreinte validée (V97.275). La date d'activation est donc celle
// de la publication app (tampon client_app_publication.published_at) ou de la
// remise (suivi_validations.document_remis_at, lue par la vue suivi_versions).
//
// Ces fonctions ne servent qu'à l'AFFICHAGE et au calcul de la semaine. Elles
// ne remplacent jamais une date existante : les phases de l'ancien circuit
// gardent exactement leur comportement.

function donneesSuivi(consultation) {
  return consultation?.followup_data ?? consultation?.followupData ?? null;
}

/** La phase cible d'une version du nouveau suivi (ou d'un brouillon repris). */
export function phaseCibleSuivi(consultation) {
  const data = donneesSuivi(consultation);
  const cible = data?.suivi_phase_cible?.to ?? data?.phaseTransitionDraft?.toPhaseId ?? null;
  return cible ? String(cible) : null;
}

/** Version activée par le nouveau suivi ? */
export function estVersionSuiviV2(consultation) {
  const data = donneesSuivi(consultation);
  return Boolean(consultation?.suivi_motif ?? consultation?.suiviMotif)
    || data?._versionProvenance?.trigger === 'suivi_v2'
    || Boolean(data?.suivi_phase_cible);
}

/** Date d'activation connue sans requête (publication app), ou null. */
export function dateActivationSuivi(consultation) {
  if (!estVersionSuiviV2(consultation)) return null;
  const publication = consultation?.client_app_publication ?? consultation?.clientAppPublication ?? null;
  return publication?.published_at || consultation?.suivi_activation_at || null;
}

/**
 * Dates d'activation par phase, pour toute la cliente : une phase activée par
 * une version du nouveau suivi commence à la PREMIÈRE sortie (publication app
 * ou remise du document) de cette version. Les versions suivantes (sans
 * changement de phase) gardent donc la date : elle ne dépend plus de la
 * version affichée.
 *   lignes  : versions (id, suivi_motif, followup_data, client_app_publication)
 *   sorties : lignes de suivi_versions (consultation_id, publiee_le,
 *             publication_tampon_le, remise_le)
 * Rend { [phaseId]: date ISO }.
 */
export function datesActivationPhases(lignes = [], sorties = []) {
  const parVersion = new Map((sorties || []).map((s) => [String(s?.consultation_id), s]));
  const dates = {};
  for (const ligne of lignes || []) {
    if (!estVersionSuiviV2(ligne)) continue;
    const cible = phaseCibleSuivi(ligne);
    if (!cible) continue;
    const sortie = parVersion.get(String(ligne.id));
    const candidates = [
      ligne?.client_app_publication?.published_at,
      sortie?.publiee_le, sortie?.publication_tampon_le, sortie?.remise_le,
    ].filter((v) => v && Number.isFinite(new Date(v).getTime()));
    for (const date of candidates) {
      if (!dates[cible] || new Date(date) < new Date(dates[cible])) dates[cible] = date;
    }
  }
  return dates;
}

function dateDe(activation, phaseId, consultation, exigerCible) {
  if (!activation) return null;
  if (typeof activation === 'object') return activation[String(phaseId)] || null;
  // Ancienne forme : une date liée à la version affichée.
  if (!estVersionSuiviV2(consultation)) return null;
  return !exigerCible || String(phaseId) === phaseCibleSuivi(consultation) ? activation : null;
}

/**
 * Date de début de la phase active quand elle manque. « activation » est la
 * table rendue par datesActivationPhases (ou, ancienne forme, la date
 * d'activation de la version affichée).
 */
export function debutPhaseActive(protocolPhases, consultation, activation) {
  const active = protocolPhases?.phases?.find?.((phase) => phase?.status === 'active');
  if (!active) return null;
  if (active.started_at) return active.started_at;
  return dateDe(activation, active.id, consultation, true);
}

/** Date de fin d'une phase terminée quand elle manque : l'activation de la suivante. */
export function finPhase(phase, protocolPhases, consultation, activation) {
  if (phase?.completed_at) return phase.completed_at;
  if (phase?.status !== 'completed') return null;
  const phases = protocolPhases?.phases || [];
  const index = phases.findIndex((item) => item === phase);
  const suivante = index >= 0 ? phases[index + 1] : null;
  if (!suivante) return null;
  if (typeof activation === 'object' && activation) return activation[String(suivante.id)] || null;
  return suivante.status === 'active' ? dateDe(activation, suivante.id, consultation, true) : null;
}
